/**
 * Signed proof playback (spec §9.5 step 9–10). The signed URL is requested
 * on demand via repositories.medication.getProofSignedUrl, never stored, and
 * the video element is the only place it is used. No public URLs exist.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Banner, Button } from '@/components/ui'
import { PlayIcon } from '@/components/icons'
import { repositories } from '@/services/repositories'
import type { MedicationIntakeEvent } from '@/types'
import { errorMessage } from '../useMedicationData'
import '../medication.css'

export type ProofPlayerProps = {
  intakeEvent: MedicationIntakeEvent
  /** Called once after playback was requested (used to log/mark viewed). */
  onPlayed?: () => void
}

type PlayerState = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable'

export function ProofPlayer({ intakeEvent, onPlayed }: ProofPlayerProps) {
  const [state, setState] = useState<PlayerState>('idle')
  const [url, setUrl] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const load = useCallback(async () => {
    if (state === 'loading') return
    setState('loading')
    setMessage(null)
    try {
      const signedUrl = await repositories.medication.getProofSignedUrl(intakeEvent)
      if (!mountedRef.current) return
      if (!signedUrl) {
        setState('unavailable')
        return
      }
      setUrl(signedUrl)
      setState('ready')
      onPlayed?.()
    } catch (error) {
      if (!mountedRef.current) return
      setMessage(errorMessage(error))
      setState('error')
    }
  }, [intakeEvent, onPlayed, state])

  if (!intakeEvent.proofObjectPath) {
    return <p className="proof-text proof-text--muted">Er is nog geen bewijsvideo geüpload.</p>
  }

  return (
    <div className="proof-player">
      {state === 'idle' || state === 'loading' ? (
        <Button variant="secondary" size="sm" loading={state === 'loading'} onClick={() => void load()}>
          <PlayIcon size={16} />
          Video laden en afspelen
        </Button>
      ) : null}
      {state === 'error' ? (
        <Banner tone="error" action={
          <Button variant="ghost" size="sm" onClick={() => void load()}>
            Opnieuw
          </Button>
        }>
          {message ?? 'De video kan niet worden geladen.'}
        </Banner>
      ) : null}
      {state === 'unavailable' ? (
        <p className="proof-text proof-text--muted">Deze video is niet meer beschikbaar.</p>
      ) : null}
      {state === 'ready' && url ? (
        <video className="proof-video" src={url} controls playsInline aria-label="Bewijsvideo afspelen" />
      ) : null}
    </div>
  )
}
